import { motion } from "framer-motion";
import { ArrowRight, PartyPopper, Instagram } from "lucide-react";
import { useLang } from "@/App";
import { copy, eventsData, CONTACT } from "@/i18n";
import { scrollToId } from "@/utils";

const Events = () => {
    const { lang } = useLang();
    const c = copy[lang].events;

    return (
        <section id="serate" data-testid="events-section" className="relative py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
                >
                    <div>
                        <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">
                            {c.label}
                        </span>
                        <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
                            {c.title}
                        </h2>
                    </div>
                    <p className="text-neutral-400 text-sm sm:text-base max-w-sm">{c.subtitle}</p>
                </motion.div>

                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {eventsData.map((e, i) => (
                        <motion.article
                            key={i}
                            data-testid={`event-card-${i}`}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.12 }}
                            className="group relative rounded-2xl border border-white/10 bg-card p-8 hover:border-brass/40 transition-colors overflow-hidden"
                        >
                            {e.image && (
                                <div className="-mx-8 -mt-8 mb-6 h-44 overflow-hidden">
                                    <img
                                        src={e.image}
                                        alt={e.title[lang]}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                            )}
                            <span className="absolute -top-4 right-4 font-display font-extrabold text-7xl text-white/[0.04] select-none">
                                0{i + 1}
                            </span>
                            <span className="font-serif italic text-lg text-brass">{e.day[lang]}</span>
                            <h3 className="mt-3 font-heading font-bold text-xl sm:text-2xl group-hover:text-crimson-bright transition-colors">
                                {e.title[lang]}
                            </h3>
                            <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{e.desc[lang]}</p>
                        </motion.article>
                    ))}
                </div>

                <motion.div
                    data-testid="events-video-block"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7 }}
                    className="mt-16 grid lg:grid-cols-2 gap-10 items-center"
                >
                    <div className="flex justify-center">
                        <div className="relative rounded-3xl overflow-hidden border border-white/10 glow-red w-full max-w-[320px] sm:max-w-[360px]">
                            <video
                                data-testid="serate-video"
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                poster="/media/serate-poster.jpg"
                                className="w-full h-auto block"
                            >
                                <source src="/media/serate.webm" type="video/webm" />
                                <source src="/media/serate.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>
                    <div className="text-center lg:text-left">
                        <span className="font-serif italic text-lg text-brass">@themarket_lugano</span>
                        <h3 className="mt-3 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl">
                            {c.videoTitle}
                        </h3>
                        <p className="mt-4 text-neutral-400 text-sm sm:text-base leading-relaxed max-w-md mx-auto lg:mx-0">
                            {c.videoText}
                        </p>
                        <a
                            data-testid="events-video-instagram-cta"
                            href={CONTACT.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white text-xs font-bold uppercase tracking-widest px-6 py-3.5"
                        >
                            <Instagram size={15} />
                            Instagram
                        </a>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6 }}
                    className="mt-6 flex justify-center"
                >
                    <a
                        data-testid="events-instagram-link"
                        href={CONTACT.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/15 hover:border-brass text-neutral-300 hover:text-brass transition-colors text-xs font-bold uppercase tracking-widest px-6 py-3"
                    >
                        <Instagram size={15} />
                        @themarket_lugano
                    </a>
                </motion.div>

                <motion.div
                    data-testid="private-event-banner"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7 }}
                    className="mt-8 relative rounded-2xl border border-crimson/40 p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between overflow-hidden"
                >
                    <img
                        src="/media/patanegra.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-noir via-noir/80 to-noir/40 pointer-events-none" />
                    <div className="relative flex items-start gap-4">
                        <PartyPopper size={28} className="text-crimson shrink-0 mt-1" />
                        <div>
                            <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl">
                                {c.privateTitle}
                            </h3>
                            <p className="mt-2 text-neutral-300 text-sm sm:text-base max-w-xl">{c.privateText}</p>
                        </div>
                    </div>
                    <button
                        data-testid="private-event-cta"
                        onClick={() => scrollToId("contact")}
                        className="group relative inline-flex items-center gap-2 rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white text-xs font-bold uppercase tracking-widest px-6 py-3.5 shrink-0"
                    >
                        {c.privateCta}
                        <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
};

export default Events;
